import { z } from "zod"
import { GRANULARITIES, metricIdSchema, metricPointSchema } from "./metric-point.js"

/**
 * ⚠ This file is mirrored in the srv repository at
 * `regdesk-srv/packages/api-contracts/src/MetricsV2/Query.ts`.
 * The contract string, the 14 module names, the granularities and the guard-rail numbers
 * must match word for word. Types cannot enforce that across repositories, so the real
 * defence is at runtime: a mismatched `SRV_V2_CONTRACT` rejects the whole response
 * (see `classify` in srv-client).
 */

export const SRV_V2_CONTRACT = "csbi.metrics.v2" as const

export const SRV_V2_MODULES = [
  "users",
  "activity",
  "moduleUsage",
  "products",
  "applications",
  "packages",
  "submissions",
  "checklists",
  "projects",
  "changes",
  "watchlists",
  "tags",
  "reportsUsage",
  "lifecycle",
] as const
export type SrvV2Module = (typeof SRV_V2_MODULES)[number]

export const srvIdentitySchema = z.object({
  contract: z.literal(SRV_V2_CONTRACT),
  host: z.string().min(1),
  env: z.string().min(1),
  srvVersion: z.string().min(1),
  availableModules: z.array(z.string()),
  availableMetrics: z.array(metricIdSchema),
  serverTime: z.iso.datetime({ offset: true }),
})
export type SrvIdentity = z.infer<typeof srvIdentitySchema>

/**
 * ⚠ **One malformed point must not discard the other 191.** The strict schema below parses the whole body in
 * one go, so a single checklist whose `countryId` read "Not stated" failed `dims.country`, failed the parse,
 * and took the entire `checklists` module down with it — five metrics went dark for three days while srv was
 * answering 200 and the other 191 points were perfectly good.
 */
export const metricsV2EnvelopeShellSchema = z.object({
  contract: z.literal(SRV_V2_CONTRACT),
  identity: z.object({
    host: z.string().min(1),
    env: z.string().min(1),
    srvVersion: z.string().min(1),
  }),
  module: z.string().min(1),
  window: z.object({
    since: z.iso.datetime({ offset: true }),
    until: z.iso.datetime({ offset: true }),
    granularity: z.enum(GRANULARITIES),
  }),
  generatedAt: z.iso.datetime({ offset: true }),
  /** How long this aggregation took. Watched so that collecting never slows down a customer's own environment */
  elapsedMs: z.number().int().nonnegative(),
  points: z.array(z.unknown()),
  /** Metrics owned by this module that this version does not implement. The collector writes the four-state from it — never fake "no data" with an empty array */
  unsupported: z.array(metricIdSchema).default([]),
  truncated: z.boolean().default(false),
})
export type MetricsV2EnvelopeShell = z.infer<typeof metricsV2EnvelopeShellSchema>

export const metricsV2EnvelopeSchema = metricsV2EnvelopeShellSchema.extend({
  points: z.array(metricPointSchema),
})
export type MetricsV2Envelope = z.infer<typeof metricsV2EnvelopeSchema>

export const srvV2QuerySchema = z.object({
  since: z.iso.datetime({ offset: true }).optional(),
  until: z.iso.datetime({ offset: true }).optional(),
  granularity: z.enum(GRANULARITIES).default("day"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export type SrvV2Query = z.infer<typeof srvV2QuerySchema>

export const MAX_WINDOW_DAYS = 400
export const MAX_POINTS_PER_RESPONSE = 50_000

export const SRV_ERROR_CLASS = {
  bad_request: "fatal",
  unauthorized: "fatal",
  unknown_module: "unsupported",
  payload_too_large: "split",
  aggregation_timeout: "retry",
  internal: "retry",
} as const
export type SrvErrorClass = (typeof SRV_ERROR_CLASS)[keyof typeof SRV_ERROR_CLASS]
